<script>
  import { onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import { overpanelMap, openOverpanel, closeOverpanel } from '../../stores/overpanelControl';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { KEYCODES } from '../../utils/constants';
  import handleFetch from '../../utils/handleFetch';
  import helpFeedbackData from '../../stores/helpFeedback';
  import { trackEvent } from '../../utils/umami-events.js';

  let {
    title,
    headingText,
    hateLabelText,
    dislikeLabelText,
    neutralLabelText,
    likeLabelText,
    loveLabelText,
    inputInstructionText,
    inputPlaceholder,
    disclaimer,
    submitButtonText,
    emailQuestionText,
    emailPlaceholder,
    emailValidationErrorText,
    skipButtonText,
    closeButtonText,
    successText,
    dataTestId = 'feedback-overpanel',
    dataTestIdHelp = 'help-feedback-overpanel',
    incomingState = 'initial',
    lang = '',
  } = $props();

  let spriteIds = ['emote-angry', 'emote-intimidated', 'emote-sceptic', 'emote-happy', 'emote-love'];
  let spriteLabels = [hateLabelText, dislikeLabelText, neutralLabelText, likeLabelText, loveLabelText];
  let headerVisible = $state(true);
  let selected = $state();
  let textArea = $state();
  let inputValue = $state();
  let inputNode = $state();
  let textAreaValue = $state();
  let submitState = $state('initial');
  let isHelpful;
  let slug;
  // Component State Management for the Email View
  const setState = (state) => {
    currentState = { ...initialStates, [state]: true };
  };

  let currentState = $state();

  const initialStates = {
    initial: false,
    populated: false,
    invalid: false,
    success: false,
  };

  setState(incomingState);

  let state = $derived(currentState);
  let submitType = $derived(submitState);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (submitState === 'skip') {
      return submitForm();
    }
    checkValidity();
    if (!state.invalid) {
      submitForm();
    }
  };

  const setSubmitState = (state) => {
    submitState = state;
  };

  const submitForm = () => {
    const overpanel = $overpanelMap.has('feedback') ? 'feedback' : 'helpFeedback';
    if (overpanel === 'feedback') {
      sendFeedback();
    } else {
      sendHelpFeedback();
    }
  };

  const sendFeedback = async () => {
    try {
      const isValidEmail = inputNode && inputNode.validity.valid && inputValue;
      await handleFetch(`${process.env.SAAS_FEEDBACK_ENDPOINT}`, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': `${process.env.SAAS_API_KEY}`,
        },
        errorKey: 'Failed to send feedback.',
        body: JSON.stringify({
          description: textAreaValue,
          rating: selected,
          agree: true,
          url: window.location.pathname,
          locale: lang,
          email: isValidEmail ? inputValue : undefined,
        }),
      });
      setState('success');
    } catch (err) {
      console.error(err); /* eslint-disable-line no-console */
    }

    trackEvent(`feedback-form-${state.success ? 'success' : 'failed'}`);
  };

  const sendHelpFeedback = async () => {
    try {
      const isValidEmail = inputNode && inputNode.validity.valid && inputValue;
      await handleFetch(`${process.env.SAAS_HELP_FEEDBACK_ENDPOINT}`, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': `${process.env.SAAS_API_KEY}`,
        },
        errorKey: 'Failed to send help page feedback.',
        body: JSON.stringify({
          isHelpful,
          isSkip: submitState === 'skip',
          slug,
          locale: lang,
          url: window.location.pathname,
          description: submitState !== 'submit' ? '' : textAreaValue || '',
          email: submitState !== 'submit' || !isValidEmail ? '' : inputValue,
        }),
      });
      setState('success');
    } catch (err) {
      console.error(err); /* eslint-disable-line no-console */
    }
    
    trackEvent(`help-feedback-form-${state.success ? 'success' : 'failed'}`);
  };

  const onChange = (e) => {
    selected = parseInt(e.target.value, 10);
  };

  const mountCallback = () => {
    selected = undefined;
    textArea.value = '';
  };

  const reset = () => {
    inputValue = '';
    setState('initial');
  };

  const handleBlur = () => {
    if (!inputNode.validity.valid) {
      if (!inputValue) {
        inputValue = '';
        setState('initial');
      } else {
        setState('invalid');
      }
    }
  };

  const handleOnKeydown = (e) => {
    if (e.keyCode === KEYCODES.ESCAPE || e.keyCode === KEYCODES.ENTER) return;
    if (state.invalid) setState('populated');
  };

  const handleCloseButtonClicked = () => {
    const overpanel = $overpanelMap.has('feedback') ? 'feedback' : 'helpFeedback';
    reset();
    closeOverpanel(overpanel);
  };

  const checkValidity = () => {
    if (!inputNode.validity.valid || !inputValue) {
      setState('invalid');
    }
  };

  onMount(() => {
    helpFeedbackData.subscribe((value) => {
      if (value) {
        isHelpful = value.isHelpful;
        slug = value.slug;
      }
    });

    let observer = new IntersectionObserver(
      (entries) => {
        headerVisible = entries[0].intersectionRatio > 0;
      },
      {
        root: null,
        rootMargin: '10px',
        threshold: 0.0,
      }
    );
    observer.observe(document.querySelector('header'));

    return () => helpFeedbackData.set(null);
  });

  let showButton = $derived(!headerVisible);
</script>

<button
  in:fly={{ x: 100, duration: 200 }}
  out:fly={{ x: 100, duration: 200 }}
  class="fixed right-0 bottom-10 lg:bottom-16 z-40 print:hidden items-center rounded-l border-t border-blue-cerulean
  bg-blue-cerulean py-4 px-2 text-center text-base leading-loose font-semibold text-white hover-hover:hover:bg-blue-darkish
  hover-hover:hover:border-blue-darkish active:bg-blue-navy active:border-blue-navy whitespace-nowrap focus:outline-hidden
  focus:shadow-focus active:shadow-active cursor-pointer"
  class:hidden={!showButton}
  data-testid={`${dataTestId}-open-button`}
  onclick={(e) => {
    e.preventDefault();
    openOverpanel('feedback');
    setState('initial');
    trackEvent('feedback-click');
  }}
>
  <div class="tracking-wider rotate-180 min-w-8" style="writing-mode: vertical-rl">{title}</div>
</button>

{#if $overpanelMap.has('feedback')}
  <OverpanelControl {title} {mountCallback} overpanelKey="feedback">
    <form
      id="feedback-form"
      class="flex flex-col min-h-full bg-gray-mercury overflow-auto"
      onsubmit={handleEmailSubmit}
      data-testid={`${dataTestId}-form`}
    >
      {#if state.success}
        <div class="flex flex-col flex-1 items-center justify-center p-4">
          <svg class="w-22 h-22 mb-7 fill-current stroke-current text-blue-cerulean">
            <use xlink:href={`${SPRITESHEET_URL}check`} />
          </svg>
          {@html successText}
        </div>
        <div class="flex justify-center lg:mb-4">
          <button
            class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
                focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex
                items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4 text-black"
            onclick={handleCloseButtonClicked}
            data-testid="{dataTestId}-close-button"
          >
            {closeButtonText}
          </button>
        </div>
      {:else}
        <div class="flex flex-col">
          <div class="flex flex-col">
            <label
              for="feedback-email-input"
              class="block text-lg leading-tight font-semibold text-gray-gunmetal px-4 pt-4 pb-2 lg:pt-6 lg:pb-4"
            >
              {emailQuestionText}
            </label>
            <div class="px-4">
              <input
                id="feedback-email-input"
                type="email"
                name="email"
                placeholder={emailPlaceholder}
                class="w-full rounded-sm border px-4 py-2 bg-white text-base leading-loose placeholder-gray-bluey"
                class:border-red={state.invalid}
                class:text-red={state.invalid}
                class:border-gray-gunmetal={state.initial}
                class:text-gray-gunmetal={state.initial}
                bind:this={inputNode}
                bind:value={inputValue}
                onblur={handleBlur}
                onkeydown={handleOnKeydown}
                required
                aria-required="true"
                data-testid="{dataTestId}-email-input"
              />
              <span
                class="text-sm text-red leading-loose"
                class:hidden={state.initial || state.populated}
                class:block={state.invalid}
                data-testid="{dataTestId}-validation-message"
              >
                {emailValidationErrorText}
              </span>
            </div>
          </div>
          <fieldset class="relative" onchange={onChange}>
            <legend class="block text-lg leading-tight font-semibold text-gray-gunmetal px-4 pt-6 pb-2 lg:pb-4">
              {headingText}
            </legend>
            <div class="flex px-2 items-center justify-around">
              {#each spriteIds as id, index (index)}
                <input
                  {id}
                  type="radio"
                  value={index + 1}
                  name="rating"
                  class="visually-hidden"
                  checked={selected === index + 1}
                  required
                  data-testid="{dataTestId}-input-{id}"
                />
                <label class="text-center group" for={id} data-testid="{dataTestId}-label-{id}">
                  <div
                    class="relative rounded-lg text-black p-2 lg:p-4 hover-hover:hover:bg-blue-cerulean hover-hover:hover:text-white
                     active:bg-blue-cerulean active:text-white active:shadow-active shadow-y-sm cursor-pointer"
                    class:bg-white={selected !== index + 1}
                    class:text-white={selected === index + 1}
                    class:bg-blue-cerulean={selected === index + 1}
                  >
                    <svg role="presentation" class="w-10 h-10 lg:w-12 lg:h-12 inline fill-current">
                      <use xlink:href={`${SPRITESHEET_URL}${id}`}/>
                    </svg>
                  </div>
                  <span
                    class="block mt-1 transition-opacity duration-200 opacity-0 hover-hover:group-hover:opacity-100 group-active:opacity-100"
                    class:opacity-100={selected === index + 1}
                  >
                    {spriteLabels[index]}
                  </span>
                </label>
              {/each}
            </div>
          </fieldset>
          <div class="flex px-4 flex-1 flex-col">
            <label for="feedback-input" class="block font-semibold text-lg text-gray-gunmetal lg:pb-2">
              {inputInstructionText}
            </label>
            <textarea
              id="feedback-input"
              name="description"
              bind:this={textArea}
              bind:value={textAreaValue}
              class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-4 py-2 bg-white text-base
                leading-loose placeholder-gray-bluey"
              placeholder={inputPlaceholder}
              rows="5"
              maxlength="1024"
              required
              aria-required="true"
              data-testid="{dataTestId}-input"
            ></textarea>
            <div class="markdown-wrapper my-4 text-0.5sm leading-tight text-gray-charcoal">
              {@html disclaimer.content}
            </div>
          </div>
          <button
            type="submit"
            onclick={() => setSubmitState('submit')}
            class="shrink-0 leading-loose text-center font-semibold text-black bg-blue-robin-egg hover-hover:hover:bg-blue-light
              active:bg-blue-malibu focus:outline-hidden focus:shadow-inset-focus active:shadow-inset-active sm:rounded-md whitespace-nowrap
              min-w-25 px-8 flex items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4 cursor-pointer"
            data-testid="{dataTestId}-submit-button"
          >
            {submitButtonText}
          </button>
        </div>
      {/if}
    </form>
  </OverpanelControl>
{/if}

{#if $overpanelMap.has('helpFeedback')}
  <OverpanelControl {title} {mountCallback} overpanelKey="helpFeedback">
    <form
      id="feedback-form"
      class="flex flex-col min-h-full bg-gray-mercury overflow-auto"
      onsubmit={handleEmailSubmit}
      data-testid={dataTestIdHelp}
    >
      {#if state.success}
        <div class="flex flex-col flex-1 items-center justify-center p-4">
          <svg class="w-22 h-22 mb-7 fill-current stroke-current text-blue-cerulean">
            <use xlink:href={`${SPRITESHEET_URL}check`} />
          </svg>
          {@html successText}
        </div>
        <div class="flex justify-center lg:mb-4">
          <button
            class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
              focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex
              items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4 text-black"
            onclick={handleCloseButtonClicked}
            data-testid="{dataTestId}-close-button"
          >
            {closeButtonText}
          </button>
        </div>
      {:else}
        <div class="flex flex-col">
          <div class="flex flex-col">
            <label
              for="feedback-form"
              class="block text-lg leading-tight font-semibold text-gray-gunmetal px-4 pt-4 lg:pt-6"
            >
              {emailQuestionText}
            </label>
            <div class="px-4">
              <input
                type="email"
                required={submitType === 'submit'}
                placeholder={emailPlaceholder}
                class="w-full rounded-sm border px-4 py-2 mt-4 bg-white text-base leading-loose placeholder-gray-bluey"
                class:border-red={state.invalid}
                class:text-red={state.invalid}
                class:border-gray-gunmetal={state.initial}
                class:text-gray-gunmetal={state.initial}
                bind:this={inputNode}
                bind:value={inputValue}
                onblur={handleBlur}
                onkeydown={handleOnKeydown}
                aria-required="true"
                data-testid="{dataTestIdHelp}-email-input"
              />
              <span
                class="text-sm text-red leading-loose"
                class:hidden={state.initial || state.populated}
                class:block={state.invalid}
                data-testid="{dataTestIdHelp}-validation-message"
              >
                {emailValidationErrorText}
              </span>
            </div>
          </div>
          <div class="flex px-4 flex-1 flex-col">
            <label for="feedback-input" class="block leading-loose font-semibold text-lg text-gray-gunmetal">
              {inputInstructionText}
            </label>
            <textarea
              id="feedback-input"
              name="description"
              required={submitType === 'submit'}
              bind:this={textArea}
              bind:value={textAreaValue}
              class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-4 py-2 bg-white text-base
              leading-loose placeholder-gray-bluey"
              placeholder={inputPlaceholder}
              rows="5"
              maxlength="1024"
              aria-required="true"
              data-testid="{dataTestIdHelp}-input"
            ></textarea>
            <div class="markdown-wrapper my-4 text-0.5sm leading-tight text-gray-charcoal">
              {@html disclaimer.content}
            </div>
          </div>
          <div class="flex flex-row px-4 justify-center">
            <button
              type="submit"
              value="submit"
              required={submitState === 'submit'}
              onclick={() => setSubmitState('submit')}
              class="shrink-0 leading-loose text-center font-semibold text-black bg-blue-robin-egg hover-hover:hover:bg-blue-light
              active:bg-blue-malibu focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap
              min-w-30 px-8 flex items-center justify-center h-16 sm:h-12 mt-0.5 sm:max-w-sm sm:my-4 cursor-pointer mr-8"
              data-testid="{dataTestIdHelp}-submit-button"
            >
              {submitButtonText}
            </button>
            <button
              type="submit"
              value="skip"
              required={submitState === 'submit'}
              onclick={() => setSubmitState('skip')}
              class="shrink-0 leading-loose text-center font-semibold text-black bg-blue-robin-egg hover-hover:hover:bg-blue-light
              active:bg-blue-malibu focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap
              min-w-30 px-8 flex items-center justify-center h-16 sm:h-12 mt-0.5 sm:max-w-sm sm:my-4 cursor-pointer"
              data-testid="{dataTestIdHelp}-skip-button"
            >
              {skipButtonText}
            </button>
          </div>
        </div>
      {/if}
    </form>
  </OverpanelControl>
{/if}
